import { useState } from "react"
import { createPortal } from "react-dom"
import parse from "html-react-parser"

import Modal from "shared/components/modal"
import SuccessModal from "app/components/billing/success_modal"
import { updateSubscriptionPlan } from "app/components/upgrade_plan_modal/pro_upgrade/api"

import iftttLogo from "images/logo-ifttt.svg?raw"
import { planNameBadge } from "shared/utils/tiers"

import styles from "app/components/upgrade_plan_modal/shared_modal.module.scss"

interface Props {
  productId: string
  planName: string
  planTier: string
  cta: string
  priceLine: string
  subscriptionPaymentType: string
  buttonOnColor?: boolean
}

// Plan-change button for users on the credits billing system: switches an
// existing Stripe subscription to another credits plan in place, mirroring
// PlanDowngradeButton (credits plans have no feature gates to enumerate, so
// the confirmation copy is just the target plan and its price).
const CreditsPlanChangeButton = ({
  productId,
  planName,
  planTier,
  cta,
  priceLine,
  subscriptionPaymentType,
  buttonOnColor = false,
}: Props) => {
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  if (subscriptionPaymentType !== "stripe") return null

  const buttonClass = `button-secondary${buttonOnColor ? " button-on-color" : ""}`

  const handleClick = (evt: MouseEvent) => {
    evt.preventDefault()
    setShowModal(true)
  }

  const handleClose = () => {
    setShowModal(false)
  }

  const handleSwitch = async () => {
    setLoading(true)

    try {
      const { errors } = await updateSubscriptionPlan({ productId })

      if (!errors?.length) {
        setShowModal(false)
        setShowSuccess(true)
      } else {
        const errorMessage = errors[0]?.message || "Something went wrong. Please try again."
        window.appendFlash(errorMessage, "danger")
        setShowModal(false)
      }
    } catch {
      window.appendFlash("Something went wrong. Please try again.", "danger")
      setShowModal(false)
    } finally {
      setLoading(false)
    }
  }

  if (showSuccess) {
    return createPortal(
      <SuccessModal
        title="You've switched to"
        badge={iftttLogo + planNameBadge(planName)}
        visible={true}
        onClose={() => {
          window.location.href = "/billing"
        }}
        buttons={[
          {
            label: "Back to Billing",
            onClick: () => {
              window.location.href = "/billing"
            },
            variant: "secondary",
          },
          {
            label: "Explore Applets",
            onClick: () => {
              window.location.href = "/explore"
            },
            variant: "primary",
          },
        ]}
      />,
      document.body,
    )
  }

  return (
    <>
      <button className={buttonClass} data-plan-tier={planTier} onClick={handleClick}>
        {cta}
      </button>
      {showModal &&
        createPortal(
          <div className={styles["modal-container"]}>
            <Modal show={showModal} closeListener={handleClose} includeCloseX={true}>
              <h2 className="mt0 mb0">Switch to</h2>
              <div className="success-modal-badge">{parse(iftttLogo + planNameBadge(planName))}</div>
              <p>
                Your subscription will be switched to the {planName} plan, {priceLine}.
              </p>
              <div className="action-buttons-row">
                <button className="button-tertiary button-outlined" onClick={handleClose}>
                  Nevermind
                </button>
                <button className="button-tertiary" disabled={loading} onClick={handleSwitch}>
                  {loading ? "Switching..." : cta}
                </button>
              </div>
            </Modal>
          </div>,
          document.body,
        )}
    </>
  )
}

export default CreditsPlanChangeButton
